// Home (PROTO build.py "home" section / built index.html). Home has no
// Opener — its own "stage" payoff plays that role instead — so this page
// renders the emblem + two-line payoff, then the five doors, each its own
// react-router Link. No arrival gate, no sound toggle (dropped per
// build-plan: "No gate, no sound").
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { BannerCanvas } from '../BannerCanvas.js';
import { gsap, reduceMotion, useGsapContext } from '../motion.js';
import './HomePage.css';

export function HomePage() {
  const ref = useRef<HTMLDivElement | null>(null);

  useGsapContext(
    ref,
    () => {
      if (reduceMotion) return;
      // the news door's own words/art fade in from the left as it scrolls by
      const newsDoor = ref.current?.querySelector('.door--black');
      if (newsDoor) {
        gsap.from(newsDoor.querySelectorAll('.d-text > *, .d-art'), {
          opacity: 0,
          x: -30,
          stagger: 0.06,
          ease: 'none',
          scrollTrigger: { trigger: newsDoor, start: 'top 60%', end: 'top 25%', scrub: true },
        });
      }
      // every other door rises in as it scrolls by
      gsap.utils.toArray<HTMLElement>('.door:not(.door--black)', ref.current ?? undefined).forEach((d) => {
        gsap.from(d.querySelectorAll('.d-text, .d-art'), {
          y: 40,
          opacity: 0,
          stagger: 0.1,
          ease: 'none',
          scrollTrigger: { trigger: d, start: 'top 90%', end: 'top 60%', scrub: true },
        });
      });
      gsap.from('.payoff .mask>span', { yPercent: 105, duration: 1.2, ease: 'expo.out', stagger: 0.12, delay: 0.1 });
      gsap.from('.emblem', { y: 40, opacity: 0, scale: 0.97, duration: 1.6, ease: 'expo.out', delay: 0.1 });
    },
    [],
  );

  return (
    <div ref={ref}>
      <section className="stage wrap" aria-labelledby="payoff">
        <img
          className="emblem"
          src="/art/fishes-solid.webp"
          alt="The Clan Heart fish emblem: a skull with a heart on its brow, on an anchor wreathed in fish"
        />
        <h1 className="payoff" id="payoff">
          <span className="mask">
            <span>The people we play with</span>
          </span>
          <span className="mask">
            <span>make the games worth playing</span>
          </span>
        </h1>
      </section>
      <div className="doors">
        <Link className="door door--parch" to="/about">
          <div className="door-in wrap">
            <div className="d-text">
              <span className="label">About Clan Heart</span>
              <h2>Respect and trust is given by default</h2>
              <p>We expect behaviour and communication of grown-ups, and we have members from all corners of the world.</p>
              <span className="go">
                Meet the crew <span aria-hidden="true">→</span>
              </span>
            </div>
            <div className="d-art flags">
              <img src="/art/flags/flag-heart-1.webp" alt="" />
              <img src="/art/flags/minori-flag.webp" alt="" />
              <img src="/art/flags/flag-koldhorn.webp" alt="" />
              <img src="/art/flags/flag-hartje.webp" alt="" />
            </div>
          </div>
        </Link>
        <Link className="door door--sea" to="/join">
          <BannerCanvas kind="cloth" />
          {/* text only: a flag is never shown on the black banner */}
          <div className="door-in door-in--solo wrap">
            <div className="d-text">
              <span className="label">Join our ranks</span>
              <h2>Safe enough to try</h2>
              <p>
                Interested in becoming a clan member? We’re committed to ensuring it’s a great fit for both you and us.
              </p>
              <span className="go">
                How to join <span aria-hidden="true">→</span>
              </span>
            </div>
          </div>
        </Link>
        <Link className="door door--ochre" to="/lore">
          <div className="door-in wrap">
            <div className="d-text">
              <span className="label">Epic Clan Lore</span>
              <h2>Music &amp; Stories</h2>
              <p>Pirate songs by Hartje.</p>
              <span className="go">
                Listen <span aria-hidden="true">→</span>
              </span>
            </div>
            <div className="d-art">
              <img src="/art/logo-ink.webp" alt="" className="small" />
            </div>
          </div>
        </Link>
        <Link className="door door--black" to="/news">
          <BannerCanvas kind="cloth" />
          <div className="door-in wrap">
            <div className="d-text">
              <span className="label">Public News Paper</span>
              <h2>Victory at Bimini – Port Battle Report</h2>
              <p>When the waves settled, 11 British ships had been sunk at the cost of just three of ours.</p>
              <span className="go">
                Read the paper <span aria-hidden="true">→</span>
              </span>
            </div>
            <div className="d-art paper">
              <span>Verenigde Prutsers Krant</span>
              <small>Printed promptly after every defeat</small>
            </div>
          </div>
        </Link>
        <Link className="door door--cave" to="/cave">
          <BannerCanvas kind="cave" />
          <div className="door-in wrap">
            <div className="d-text">
              <span className="label">Hidden Treasure Cave</span>
              <h2>Crew only</h2>
              <p>Sign in with your Discord account to enter.</p>
              <span className="go">
                Enter the cave <span aria-hidden="true">→</span>
              </span>
            </div>
            <div className="d-art cave-mark" aria-hidden="true">
              <svg viewBox="0 0 60 90" width="120" height="180">
                <circle cx="30" cy="26" r="20" fill="currentColor" />
                <path d="M19 38 L41 38 L48 88 L12 88 Z" fill="currentColor" />
              </svg>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
